import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, DownloadSimple, LinkSimple, Key, Copy, CheckCircle, Handshake, PencilSimple, Desktop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PublicNav } from "@/components/layout/PublicNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { coverSrc, productPrice } from "@/components/agent/ProductCard";
import { ShareBar, useTitle } from "@/components/growth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Icon } from "@/lib/icons";
import { API, api, errMsg, startLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const kb = (n) => (!n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const RequestDialog = ({ open, onOpenChange, productId, onDone }) => {
  const [f, setF] = useState({ company: "", phone: "", message: "" });
  const send = async () => {
    try { await api.post(`/products/${productId}/request`, f); toast.success("Solicitud enviada. Te contactaremos pronto."); onDone(); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm border-white/10 bg-[#0f0f0f] text-white" data-testid="contract-request-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Solicitar contrato</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="label">Empresa (opcional)</label><input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="field" data-testid="request-company-input" /></div>
          <div><label className="label">Teléfono (opcional)</label><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="field" data-testid="request-phone-input" /></div>
          <div><label className="label">¿Qué necesitas?</label><textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={5} className="field" placeholder="Número de licencias, uso previsto, plazos…" data-testid="request-message-input" /></div>
          <button onClick={send} disabled={f.message.trim().length < 5} className="btn-primary w-full" data-testid="request-submit-button">Enviar solicitud</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const LicenseKey = ({ value }) => (
  <div className="flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="license-key-box">
    <Key size={16} className="shrink-0 text-cyan-400" />
    <code className="flex-1 truncate font-mono text-sm text-cyan-200" data-testid="license-key-value">{value}</code>
    <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Licencia copiada"); }} className="p-1 text-zinc-400 hover:text-white" data-testid="license-copy-button"><Copy size={15} /></button>
  </div>
);

export const AssetList = ({ productId, assets }) => (
  <div className="border border-white/10" data-testid="asset-list">
    {assets.map((a) => (
      <a key={a.asset_id} href={`${API}/products/${productId}/assets/${a.asset_id}/download`} target={a.type === "link" ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-3 border-b border-white/5 px-4 py-3 last:border-b-0 hover:bg-white/[0.03]" data-testid={`asset-download-${a.asset_id}`}>
        {a.type === "link" ? <LinkSimple size={18} className="text-cyan-400" /> : <DownloadSimple size={18} className="text-blue-500" />}
        <span className="flex-1 truncate text-sm">{a.label}</span>
        <span className="font-mono text-[11px] text-zinc-500">{a.type === "link" ? "Enlace externo" : kb(a.size)}</span>
      </a>
    ))}
    {assets.length === 0 && <p className="p-4 text-sm text-zinc-500">El creador aún no ha añadido archivos.</p>}
  </div>
);

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const { data: p, isError, isLoading } = useQuery({ queryKey: ["product", productId], queryFn: async () => (await api.get(`/products/${productId}`)).data });
  const { data: access, refetch } = useQuery({ queryKey: ["paccess", productId, user?.user_id], enabled: !!user, queryFn: async () => (await api.get(`/products/${productId}/access`)).data });
  useTitle(p?.name);

  const acquire = async () => {
    if (!user) return startLogin(`/product/${productId}`);
    if (p.pricing_type === "contract") return setReqOpen(true);
    setBusy(true);
    try {
      if (p.pricing_type === "free") { await api.post(`/products/${productId}/acquire-free`); toast.success("Añadido a tu biblioteca"); refetch(); }
      else { const { data } = await api.post(`/products/${productId}/checkout`, { origin_url: window.location.origin }); window.location.href = data.checkout_url; return; }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (isLoading) return <div className="min-h-screen bg-[#050505]"><PublicNav /><div className="h-px bg-white/10 thinking-beam" /></div>;
  if (isError || !p) return <div className="min-h-screen bg-[#050505] text-white"><PublicNav /><div className="mx-auto max-w-7xl px-6 py-24"><h1 className="font-display text-4xl font-bold">Producto no disponible</h1><Link to="/" className="btn-ghost mt-6">Volver</Link></div></div>;

  const label = { free: "Obtener gratis", contract: "Solicitar contrato", monthly: `Suscribirme · ${productPrice(p)}`, one_time: `Comprar · ${productPrice(p)}` }[p.pricing_type];
  const pending = access?.request_status === "pending";

  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <PublicNav />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="product-back-button"><ArrowLeft size={16} /> Volver</button>
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-7">
            <div className="reveal">
              <p className="eyebrow mb-3">{p.kind === "model" ? "Modelo local" : "Software"} · {p.category}</p>
              <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl" data-testid="product-detail-name">{p.name}</h1>
              <p className="mt-3 text-lg text-zinc-400">{p.tagline}</p>
              <div className="mt-6"><ShareBar title={p.name} /></div>
            </div>
            <div className="relative aspect-[16/9] overflow-hidden border border-white/10" style={{ background: `${p.color}10` }}>
              {coverSrc(p) ? <img src={coverSrc(p)} alt={p.name} className="h-full w-full object-cover" /> : <div className="grid-bg grid h-full place-items-center"><Icon name={p.icon} size={88} weight="duotone" style={{ color: p.color }} /></div>}
            </div>
            {p.description && <p className="whitespace-pre-line leading-relaxed text-zinc-300" data-testid="product-description">{p.description}</p>}
            {p.features?.length > 0 && (
              <div><p className="label">Características</p>
                <ul className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">{p.features.map((f) => <li key={f} className="flex gap-2 bg-[#080808] p-4 text-sm"><CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />{f}</li>)}</ul>
              </div>
            )}
            {p.specs?.length > 0 && (
              <div><p className="label">Especificaciones</p>
                <div className="border border-white/10" data-testid="product-specs">{p.specs.map((s) => <div key={s.label} className="grid grid-cols-[180px_1fr] border-b border-white/5 px-4 py-3 text-sm last:border-b-0"><span className="text-zinc-500">{s.label}</span><span className="font-mono">{s.value}</span></div>)}</div>
              </div>
            )}
            {p.requirements && <div><p className="label">Requisitos</p><p className="whitespace-pre-line border border-white/10 bg-[#080808] p-4 font-mono text-xs leading-relaxed text-zinc-300">{p.requirements}</p></div>}
            {p.changelog && <div><p className="label">Historial de versiones</p><p className="whitespace-pre-line border border-white/10 bg-[#080808] p-4 font-mono text-xs leading-relaxed text-zinc-400">{p.changelog}</p></div>}
          </div>
          <div className="lg:col-span-5">
            <div className="sticky top-24 space-y-4">
              <div className="panel p-6" data-testid="product-buy-panel">
                <p className="text-[11px] uppercase tracking-widest text-zinc-500">Precio</p>
                <p className="mt-1 font-mono text-3xl font-semibold" data-testid="product-detail-price">{productPrice(p)}</p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs text-zinc-400">
                  <span className="border border-white/10 px-2 py-1 font-mono">v{p.version}</span>
                  {p.platforms?.map((x) => <span key={x} className="inline-flex items-center gap-1 border border-white/10 px-2 py-1"><Desktop size={12} />{x}</span>)}
                  {p.license_enabled && <span className="inline-flex items-center gap-1 border border-cyan-500/30 px-2 py-1 text-cyan-400"><Key size={12} /> Licencia incluida</span>}
                </div>
                {p.pricing_type === "contract" && p.contract_terms && <p className="mt-4 whitespace-pre-line border-l-2 border-amber-500 pl-3 text-xs text-zinc-400" data-testid="product-contract-terms">{p.contract_terms}</p>}
                <div className="mt-6 space-y-3">
                  {access?.is_admin && <Link to={`/admin/products/${productId}`} className="btn-ghost w-full" data-testid="product-admin-edit"><PencilSimple size={16} /> Editar producto</Link>}
                  {access?.owned ? (
                    <>
                      {access.license_key && <LicenseKey value={access.license_key} />}
                      <AssetList productId={productId} assets={access.assets} />
                    </>
                  ) : pending ? (
                    <div className="flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300" data-testid="product-request-pending"><Handshake size={16} /> Solicitud enviada · pendiente de respuesta</div>
                  ) : (
                    <button onClick={acquire} disabled={busy} className="btn-primary w-full !py-3.5 text-base" data-testid="product-acquire-button">{busy ? "Procesando…" : label}</button>
                  )}
                </div>
                {!access?.owned && p.pricing_type !== "contract" && <p className="mt-3 text-xs text-zinc-500">{p.pricing_type === "free" ? "Requiere iniciar sesión" : "Pago seguro con Stripe · Descarga inmediata"}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
      <RequestDialog open={reqOpen} onOpenChange={setReqOpen} productId={productId} onDone={refetch} />
    </div>
  );
}
